import type {
  AnalyticsRangeKey,
  RevenueTimeseriesPoint,
} from '@shared/api-client';
import { formatCurrencyMinor } from '@/lib/utils';

export type RevenueChartMetric =
  'netCollectedMinor' | 'grossCollectedMinor' | 'refundsMinor';

const WIDTH = 900;
const HEIGHT = 280;
const PADDING_X = 18;
const PADDING_Y = 24;

const METRIC_LABELS: Record<RevenueChartMetric, string> = {
  netCollectedMinor: 'net collected',
  grossCollectedMinor: 'gross collected',
  refundsMinor: 'refunds',
};

const formatTimestamp = (value: string, compactTime: boolean): string => {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return value;
  }

  return compactTime
    ? date.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
      })
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export const RevenueLineChart = ({
  points,
  metric,
  currency,
  range,
}: {
  points: RevenueTimeseriesPoint[];
  metric: RevenueChartMetric;
  currency: string;
  range: AnalyticsRangeKey;
}) => {
  const normalizedPoints =
    points.length > 1
      ? points
      : [
          points[0] ?? {
            timestamp: new Date().toISOString(),
            currency,
            grossCollectedMinor: 0,
            refundsMinor: 0,
            netCollectedMinor: 0,
            payments: 0,
          },
          points[0] ?? {
            timestamp: new Date().toISOString(),
            currency,
            grossCollectedMinor: 0,
            refundsMinor: 0,
            netCollectedMinor: 0,
            payments: 0,
          },
        ];
  const values = normalizedPoints.map((point) => point[metric]);
  const minimum = Math.min(0, ...values);
  const observedMaximum = Math.max(0, ...values);
  const maximum =
    observedMaximum === minimum ? observedMaximum + 1 : observedMaximum;
  const span = Math.max(maximum - minimum, 1);
  const chartPoints = values.map((value, index) => ({
    x: PADDING_X + (index / (values.length - 1)) * (WIDTH - PADDING_X * 2),
    y: PADDING_Y + ((maximum - value) / span) * (HEIGHT - PADDING_Y * 2),
    value,
  }));
  const zeroY = PADDING_Y + ((maximum - 0) / span) * (HEIGHT - PADDING_Y * 2);
  const line = chartPoints
    .map(({ x, y }, index) => `${index === 0 ? 'M' : 'L'} ${x} ${y}`)
    .join(' ');
  const area = `${line} L ${chartPoints.at(-1)?.x ?? WIDTH - PADDING_X} ${zeroY} L ${PADDING_X} ${zeroY} Z`;
  const labelIndexes = Array.from(
    new Set([
      0,
      Math.floor((normalizedPoints.length - 1) / 2),
      normalizedPoints.length - 1,
    ]),
  ).filter((index) => index >= 0 && normalizedPoints[index]);
  const strokeClass =
    metric === 'refundsMinor' ? 'stroke-critical' : 'stroke-accent';
  const fillClass =
    metric === 'refundsMinor' ? 'fill-critical/8' : 'fill-accent/8';

  return (
    <div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="h-[260px] w-full"
        preserveAspectRatio="none"
        role="img"
        aria-label={`${METRIC_LABELS[metric]} trend in ${currency}`}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
          const y = PADDING_Y + fraction * (HEIGHT - PADDING_Y * 2);
          const value = maximum - span * fraction;
          return (
            <g key={fraction}>
              <line
                x1={PADDING_X}
                x2={WIDTH - PADDING_X}
                y1={y}
                y2={y}
                className="stroke-border"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
              <text
                x={PADDING_X + 4}
                y={Math.max(y - 6, 12)}
                className="fill-ink-subtle text-[10px]"
              >
                {formatCurrencyMinor(Math.round(value), currency, true)}
              </text>
            </g>
          );
        })}

        {minimum < 0 && maximum > 0 ? (
          <line
            x1={PADDING_X}
            x2={WIDTH - PADDING_X}
            y1={zeroY}
            y2={zeroY}
            className="stroke-border-strong"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
        <path d={area} className={fillClass} />
        <path
          d={line}
          fill="none"
          className={strokeClass}
          strokeWidth="2.25"
          vectorEffect="non-scaling-stroke"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {chartPoints.map((point, index) => (
          <circle
            key={`${point.x}-${point.y}`}
            cx={point.x}
            cy={point.y}
            r="7"
            className="fill-transparent"
          >
            <title>
              {`${formatTimestamp(normalizedPoints[index].timestamp, range === '24h')}: ${formatCurrencyMinor(point.value, currency)}`}
            </title>
          </circle>
        ))}
      </svg>

      <div className="mt-1 flex justify-between px-4 text-xs text-ink-subtle">
        {labelIndexes.map((index) => (
          <span key={`${normalizedPoints[index].timestamp}-${index}`}>
            {formatTimestamp(
              normalizedPoints[index].timestamp,
              range === '24h',
            )}
          </span>
        ))}
      </div>
    </div>
  );
};
